import { useEffect, useRef } from "react";
import { api } from "@/lib/api";
import {
  playNewOrder,
  playBulkOrder,
  playNewReview,
  playUpdate,
  isUnlocked,
} from "@/lib/adminSounds";

const POLL_MS = 15000;

// Polls the three admin feeds every 15s, detects deltas since the previous
// poll, and plays the matching distinct sound. First tick primes the caches
// silently so we don't blast a sound the moment the dashboard mounts.
export function useAdminEventPoller(enabled) {
  const seen = useRef({
    orderIds: null,
    orderStatus: new Map(),
    bulkIds: null,
    reviewIds: null,
  });

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    async function tick() {
      try {
        const [oRes, bRes, rRes] = await Promise.all([
          api.get("/admin/orders").catch(() => ({ data: [] })),
          api.get("/admin/bulk-orders").catch(() => ({ data: [] })),
          api.get("/admin/reviews").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const orders = oRes.data || [];
        const bulks = bRes.data || [];
        const reviews = rRes.data || [];
        const s = seen.current;

        // First poll — populate caches silently.
        if (!s.orderIds) {
          s.orderIds = new Set(orders.map((o) => o.id));
          for (const o of orders) s.orderStatus.set(o.id, o.status);
          s.bulkIds = new Set(bulks.map((b) => b.id));
          s.reviewIds = new Set(reviews.map((r) => r.id));
          return;
        }
        if (!isUnlocked()) return;

        // Only fire ONE sound per tick — priority: new order > bulk > review > status change.
        // (Prevents cacophony if several events land at once.)
        let played = null;

        for (const o of orders) {
          if (!s.orderIds.has(o.id)) {
            s.orderIds.add(o.id);
            s.orderStatus.set(o.id, o.status);
            if (!played) played = "order";
          } else if (s.orderStatus.get(o.id) !== o.status) {
            s.orderStatus.set(o.id, o.status);
            if (!played) played = "update";
          }
        }
        for (const b of bulks) {
          if (!s.bulkIds.has(b.id)) {
            s.bulkIds.add(b.id);
            if (played !== "order") played = "bulk";
          }
        }
        for (const r of reviews) {
          if (!s.reviewIds.has(r.id)) {
            s.reviewIds.add(r.id);
            if (played === null || played === "update") played = "review";
          }
        }

        if (played === "order") playNewOrder();
        else if (played === "bulk") playBulkOrder();
        else if (played === "review") playNewReview();
        else if (played === "update") playUpdate();
      } catch (_e) {
        // Swallow — try again on the next tick.
      }
    }

    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled]);
}
